
import { useIsMounted } from '@vmos2/utils';
import type { Context, ReactElement } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useDiscount, useUserPoints, useUserPromotions, useUserRewards } from './hooks';
import { useAppType } from './AppType';
import { useUser } from './User';

type UseUserPoints = ReturnType<typeof useUserPoints>;
type UseUserPromotions = ReturnType<typeof useUserPromotions>;
type UseUserRewards = ReturnType<typeof useUserRewards>;

type LoyaltyProviderContext = {
  General: {
    fetchLoyalty: (cache?: { override?: boolean; ttl?: number; }) => Promise<{
      points: UseUserPoints['userPoints'];
      promotions: UseUserPromotions['userPromotions'];
      rewards: UseUserRewards['userRewards'];
    } | null>;
    isLoadingLoyalty: boolean;
  };
  Discount: Omit<ReturnType<typeof useDiscount>, 'clearDiscount'>;
  Points: Omit<UseUserPoints, 'clearUserPoints'>;
  Promotions: Omit<UseUserPromotions, 'clearUserPromotions'>;
  Rewards: Omit<UseUserRewards, 'clearUserRewards'>;
};

const LoyaltyContext: { [K in keyof LoyaltyProviderContext]: Context<LoyaltyProviderContext[K] | undefined> } = {
  General: createContext<LoyaltyProviderContext['General'] | undefined>(undefined),
  Discount: createContext<LoyaltyProviderContext['Discount'] | undefined>(undefined),
  Points: createContext<LoyaltyProviderContext['Points'] | undefined>(undefined),
  Promotions: createContext<LoyaltyProviderContext['Promotions'] | undefined>(undefined),
  Rewards: createContext<LoyaltyProviderContext['Rewards'] | undefined>(undefined),
};

type ProviderProps = {
  children: ReactElement;
};

const LoyaltyProvider = ({ children }: ProviderProps) => {
  const { isKiosk } = useAppType();
  const isMounted = useIsMounted();
  const isLoyaltyEnabled = useRef(false);
  const previousUserUUID = useRef<string | undefined>();
  const [isLoadingLoyalty, setIsLoadingLoyalty] = useState(false);
  const { isLoggedIn, user: { userData, userLoading } } = useUser();
  const { clearUserRewards, fetchUserRewards, isLoadingUserRewards, userRewards } = useUserRewards();

  const { clearDiscount, discountInitialState, isApplyingDiscount, setDiscountInitialState, setIsApplyingDiscount } =
    useDiscount();

  const { clearUserPoints, fetchUserPoints, isLoadingUserPoints, pollUpdatePoints, userPoints } =
    useUserPoints(isLoyaltyEnabled);

  const {
    clearUserPromotions,
    fetchUserPromotions,
    isLoadingUserPromotions,
    promotionsBeingApplied,
    setPromotionsBeingApplied,
    totalPromoCount,
    userPromotions,
    userPromotionsMeta,
  } = useUserPromotions(isLoyaltyEnabled);

  const fetchLoyalty = useCallback(
    async (cache: { override?: boolean, ttl?: number } = { override: true, ttl: 3600000 }) => {
      if (isLoggedIn) {
        setIsLoadingLoyalty(true);

        const [{ pointsData, promotionsData }, rewardsData] = await Promise.all([
          // We need to know if loyalty is enabled before we fetch the promotions, which is done via the
          // `isLoyaltyEnabled` ref that gets set within `fetchUserPoints`.
          fetchUserPoints({ cache }).then(async pointsResponse => {
            const promotionsResponse = await fetchUserPromotions({ cache });

            return { pointsData: pointsResponse, promotionsData: promotionsResponse };
          }),
          fetchUserRewards({ cache }),
        ]);

        if (isMounted.current) {
          setIsLoadingLoyalty(false);
        }

        return { points: pointsData, promotions: promotionsData, rewards: rewardsData };
      }

      return Promise.resolve(null);
    },
    [fetchUserPoints, fetchUserPromotions, fetchUserRewards, isLoggedIn, isMounted]
  );

  const general = useMemo(() => ({ fetchLoyalty, isLoadingLoyalty }), [fetchLoyalty, isLoadingLoyalty]);

  const discount = useMemo(
    () => ({ discountInitialState, isApplyingDiscount, setDiscountInitialState, setIsApplyingDiscount }),
    [discountInitialState, isApplyingDiscount, setDiscountInitialState, setIsApplyingDiscount]
  );

  const points = useMemo(
    () => ({ fetchUserPoints, isLoadingUserPoints, pollUpdatePoints, userPoints }),
    [fetchUserPoints, isLoadingUserPoints, pollUpdatePoints, userPoints]
  );

  const promotions = useMemo(
    () => ({
      fetchUserPromotions,
      isLoadingUserPromotions,
      promotionsBeingApplied,
      setPromotionsBeingApplied,
      totalPromoCount,
      userPromotions,
      userPromotionsMeta,
    }),
    [
      fetchUserPromotions,
      isLoadingUserPromotions,
      promotionsBeingApplied,
      setPromotionsBeingApplied,
      totalPromoCount,
      userPromotions,
      userPromotionsMeta,
    ]
  );

  const rewards = useMemo(
    () => ({ fetchUserRewards, isLoadingUserRewards, userRewards }),
    [fetchUserRewards, isLoadingUserRewards, userRewards]
  );

  useEffect(() => {
    if (userData?.uuid && !previousUserUUID.current) {
      previousUserUUID.current = userData?.uuid;

      // We don't need to fetch it on page load for kiosk mode because the user will never be logged in at that point.
      if (!isKiosk) {
        fetchLoyalty({ override: false, ttl: 3600000 });
      }
    }

    if (!userData?.uuid && previousUserUUID.current) {
      previousUserUUID.current = undefined;
      isLoyaltyEnabled.current = false;
      clearDiscount();
      clearUserPoints();
      clearUserPromotions();
      clearUserRewards();
    }
  }, [clearDiscount, clearUserPoints, clearUserPromotions, clearUserRewards, fetchLoyalty, isKiosk, userData?.uuid]);

  if (userLoading) {
    return null;
  }

  return (
    <LoyaltyContext.General.Provider value={general}>
      <LoyaltyContext.Points.Provider value={points}>
        <LoyaltyContext.Discount.Provider value={discount}>
          <LoyaltyContext.Promotions.Provider value={promotions}>
            <LoyaltyContext.Rewards.Provider value={rewards}>
              {children}
            </LoyaltyContext.Rewards.Provider>
          </LoyaltyContext.Promotions.Provider>
        </LoyaltyContext.Discount.Provider>
      </LoyaltyContext.Points.Provider>
    </LoyaltyContext.General.Provider>
  );
};

const useLoyaltyContext = <T extends keyof typeof LoyaltyContext>(selector: T) => {
  const context = useContext(LoyaltyContext[selector]);

  if (context === undefined) {
    throw new Error('useLoyaltyContext must be used within LoyaltyProvider');
  }

  return context;
};

export type { LoyaltyProviderContext };
export { LoyaltyProvider, useLoyaltyContext };
